import axios from "axios";
import store from "@/redux/store";
import { setUser } from "@/slices/userSlice";
import { getCookie } from "@/utils/cookieUtils";

const goApiBase = import.meta.env.VITE_GO_API_BASE_URL || import.meta.env.VITE_GO_API_URL;
const baseURL = import.meta.env.VITE_MCP_URL || "";

export const isMcpConfigured = !!import.meta.env.VITE_MCP_URL;

const baseMcpApi = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

baseMcpApi.interceptors.request.use((config) => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
    return config;
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const RENEW_TIMEOUT_MS = 60000;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

const refreshToken = async (): Promise<string> => {
    const currentPath = window.location.pathname;
    const publicRoutes = ['/login', '/privacy-policy'];
    const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

    if (isPublicRoute) {
        throw new Error("No valid session on public route");
    }

    const doRenew = async (): Promise<string> => {
        const response = await axios.post(
            goApiBase + `/go-api/auth/renew_jwt`,
            {},
            {
                params: { ts: Date.now() },
                withCredentials: true,
                timeout: RENEW_TIMEOUT_MS,
                headers: {
                    "X-CSRF-Token": getCookie("csrf_token") ?? "",
                    "Cache-Control": "no-cache, no-store, must-revalidate",
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        const newToken = response.data?.jwt_token;
        const msg = response.data?.message ?? "";
        if (!newToken) {
            const err: any = new Error(msg || "No jwt_token in response");
            err.response = response;
            throw err;
        }
        return newToken;
    };

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            const newToken = await doRenew();
            localStorage.setItem("jwt_token", newToken);
            return newToken;
        } catch (error: any) {
            const status = error?.response?.status;
            const msg = error?.response?.data?.message ?? "";
            const isNetworkOrTimeout =
                !error?.response &&
                (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || error?.message?.includes('timeout'));

            if (status === 401 || status === 400 || status === 403 || msg.toLowerCase().includes("csrf")) {
                console.error("Token refresh failed (invalid session). Logging out user.", error);
                localStorage.removeItem("jwt_token");
                store.dispatch(setUser(null));
                throw error;
            }

            if (isNetworkOrTimeout && attempt < MAX_RETRIES) {
                console.warn(`Token refresh attempt ${attempt}/${MAX_RETRIES} failed (backend may be waking up), retrying...`, error?.message || error);
                await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
                continue;
            }

            console.error("Token refresh failed. Logging out user.", error);
            localStorage.removeItem("jwt_token");
            store.dispatch(setUser(null));
            throw error;
        }
    }

    throw new Error("Token refresh failed after retries");
};

const onRefreshed = (token: string) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
};

baseMcpApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve) => {
                    refreshSubscribers.push((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(axios(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newToken = await refreshToken();
                isRefreshing = false;
                onRefreshed(newToken);

                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                throw refreshError;
            }
        }

        return Promise.reject(error);
    }
);

export default baseMcpApi;
